// src/components/RefundCreateModal.js
// « Nouveau remboursement » from the refunds page (KI-158: a refund could only
// be typed inside a ticket's side tab). Step 1 finds the ticket by an exact
// reference — ticket id, external ticket id, tracking number or order — and
// shows what the refund will be tied to (client, order, carrier). Step 2 is
// the shared RefundForm, prefilled with the ticket's declared values as
// one-click suggestions.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import RefundForm from './RefundForm';
import { newRefundForm, refundPayload } from '../utils/refunds';
import './Refunds.css';

const TicketSummary = ({ ticket, t }) => (
  <div className="rfm-ticket-summary">
    <strong>
      {ticket.external_ticket_id
        ? t('refunds.ticketRef', 'Ticket {{id}}', { id: ticket.external_ticket_id })
        : t('refunds.ticketRef', 'Ticket {{id}}', { id: ticket.ticket_id.slice(-8) })}
    </strong>
    <span>
      {[ticket.client_name, ticket.order_reference && t('refunds.orderShort', 'Cde {{order}}', { order: ticket.order_reference }),
        ticket.carrier_name, ticket.tracking_number].filter(Boolean).join(' · ') || '—'}
    </span>
  </div>
);

const RefundCreateModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` }), []);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = no search yet
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const search = async (event) => {
    event?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setError('');
    try {
      const response = await axios.get('/api/logistics/refunds/ticket-lookup', { headers, params: { q } });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setResults(items);
      if (items.length === 1) pick(items[0]);
    } catch (err) {
      console.error('Refund ticket lookup failed:', err);
      setError(err?.response?.data?.detail || t('refunds.lookupError', 'La recherche a échoué.'));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const pick = (ticket) => {
    setSelected(ticket);
    setForm(newRefundForm(ticket));
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!selected || !form) return;
    setSaving(true);
    setError('');
    try {
      const response = await axios.post(`/api/logistics/tickets/${selected.ticket_id}/refunds`, refundPayload(form), { headers });
      onCreated(response.data);
    } catch (err) {
      console.error('Failed to create refund:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('refunds.createError', 'Impossible de créer le remboursement.'));
      setSaving(false);
    }
  };

  return (
    <div className="rfm-backdrop" onMouseDown={() => !saving && onClose()}>
      <div className="rfm-modal" role="dialog" aria-modal="true" aria-labelledby="rfm-title" onMouseDown={(e) => e.stopPropagation()}>
        <div className="rfm-modal-header">
          <h3 id="rfm-title">{t('refunds.createTitle', 'Enregistrer un remboursement')}</h3>
          <button type="button" className="rfm-icon-btn" onClick={onClose} disabled={saving} aria-label={t('refunds.cancel', 'Annuler')}>
            <FaTimes />
          </button>
        </div>

        {!selected ? (
          <div className="rfm-modal-body">
            <form className="rfm-lookup" onSubmit={search}>
              <label className="rfm-field" htmlFor="rfm-lookup-q">
                <span>{t('refunds.lookupLabel', 'Ticket, commande, numéro de suivi ou ID ticket externe')}</span>
                <div className="rfm-lookup-row">
                  <input
                    id="rfm-lookup-q"
                    ref={inputRef}
                    type="text"
                    maxLength={100}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('refunds.lookupPlaceholder', 'ex. 1011751942, XW123456789FR, 217069')}
                  />
                  <button type="submit" className="u-btn u-btn--secondary" disabled={searching || !query.trim()}>
                    <FaSearch /> {searching ? t('refunds.lookupSearching', 'Recherche…') : t('refunds.lookupSearch', 'Rechercher')}
                  </button>
                </div>
              </label>
              <p className="rfm-hint">{t('refunds.lookupHint', 'Correspondance exacte sur l’identifiant du ticket, l’ID externe, le numéro de suivi ou la référence de commande.')}</p>
            </form>

            {error && <div className="rfm-error" role="alert">{error}</div>}

            {results && results.length === 0 && !error && (
              <p className="rfm-empty">{t('refunds.lookupNoResult', 'Aucun ticket ne correspond exactement à cette référence.')}</p>
            )}
            {results && results.length > 0 && (
              <ul className="rfm-results">
                {results.map((ticket) => (
                  <li key={ticket.ticket_id}>
                    <button type="button" className="rfm-result" onClick={() => pick(ticket)}>
                      <TicketSummary ticket={ticket} t={t} />
                      {ticket.refunds_count > 0 && (
                        <span className="rfm-badge">
                          {t('refunds.existingCount', '{{count}} remboursement(s) déjà', { count: ticket.refunds_count })}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <form className="rfm-modal-form" onSubmit={submit}>
            <div className="rfm-modal-body">
              <div className="rfm-selected">
                <TicketSummary ticket={selected} t={t} />
                <button type="button" className="rfm-link" onClick={() => { setSelected(null); setForm(null); }} disabled={saving}>
                  <FaArrowLeft /> {t('refunds.changeTicket', 'Changer')}
                </button>
              </div>
              {selected.refunds_count > 0 && (
                <p className="rfm-hint rfm-hint--warn">
                  {t('refunds.existingWarning', 'Ce ticket a déjà {{count}} remboursement(s) : vérifiez qu’il ne s’agit pas du même avant d’en créer un autre.', { count: selected.refunds_count })}
                </p>
              )}
              <RefundForm
                form={form}
                onChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
                declared={selected.declared}
                ticketOrder={selected.order_reference}
                idPrefix="rfm-create"
              />
              {error && <div className="rfm-error" role="alert">{error}</div>}
            </div>
            <div className="rfm-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={onClose} disabled={saving}>
                {t('refunds.cancel', 'Annuler')}
              </button>
              <button type="submit" className="u-btn u-btn--primary" disabled={saving}>
                <FaPlus /> {saving ? t('refunds.saving', 'Enregistrement…') : t('refunds.createRefund', 'Créer le remboursement')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RefundCreateModal;
